import React from "react";

import {CheckIcon} from "@heroicons/react/20/solid";
import {bindActionCreators} from "@reduxjs/toolkit";
import {Decimal} from "decimal.js";
import {LayoutGroup, motion} from "framer-motion";

import * as foods from "@client/features/foods";
import {notify} from "@client/features/notifications";
import {
    changeLastUsedStore,
    changeSearchFilter,
    changeUSDA,
    fetchSearch,
    toggleBulk,
} from "@client/features/search";

import * as analytics from "@client/shared/analytics";
import * as utils from "@client/utils";

import {
    calculateEntryMetrics,
    getRoundedCalculations,
    getRoundedQuantity,
} from "@client/calculations";
import {
    ACTION_BAR_BUTTON_CLASS,
    BASE_ACTION_BAR_BUTTON_CLASS,
} from "@client/components/ActionBar";
import {SearchResultCard} from "@client/components/EntryCard";
import {Icon, SimpleIcon} from "@client/components/Icon";
import {type Handler as StageHandler} from "@client/components/Stage";
import {QuickSelect} from "@client/forms";
import type {ActionDefinition, ConditionsTab} from "@client/gotterdammerung";
import * as hardware from "@client/hardware";
import {useMedia} from "@client/hooks";
import {ScrollContainerContext} from "@client/journeys/contexts";
import {useSiteContext} from "@client/site/context";
import * as forms from "@client/site/forms";
import {useAppDispatch, useAppSelector} from "@client/store";
import {classNames, models, server} from "@reactivated";

import type {
    EntryLocator,
    PredictionsHandler,
    SelectedRootHandler,
} from "./predictions";
import * as routes from "./routes";

type ExpandedEntry = server.journal.nutrition.ExpandedEntry.output;
type SearchResult = models["journal.nutrition.SearchResult"];

function locatorToSearchResult(
    locator: EntryLocator,
    results: models["journal.nutrition.SearchResult"][],
) {
    // Split on last colon (backend_id may contain colons)
    const lastColonIndex = locator.lastIndexOf(":");
    const backend_id = locator.slice(0, lastColonIndex);
    const backend = locator.slice(lastColonIndex + 1);

    const result = results.find(
        (r) => r.backend_id === backend_id && r.backend === backend,
    );

    return {
        ...result!,
        locator,
    };
}

/**
 * Resolves locators to entries (with stable UUIDs) and syncs them into stage.
 * Optionally includes an additional search result entry.
 */
function loadEntriesIntoStage(
    locators: string[],
    recentStore: SearchResult[],
    stage: StageHandler<ExpandedEntry>,
    searchResult?: SearchResult,
) {
    const entriesFromLocators = locators.map((locator) => {
        const result = locatorToSearchResult(locator, recentStore);
        return {
            ...result.entry,
            nutritional_vision_prompt: null,
            backend: result.backend,
            backend_id: result.backend_id,
        };
    });

    const allEntries =
        searchResult != null
            ? [
                  ...entriesFromLocators,
                  {
                      ...searchResult.entry,
                      nutritional_vision_prompt: null,
                      backend: searchResult.backend,
                      backend_id: searchResult.backend_id,
                  },
              ]
            : entriesFromLocators;

    if (allEntries.length > 0) {
        stage.syncEntries(allEntries);
    }
}

function EmptyState(props: {children: React.ReactNode}) {
    return (
        <div
            // We use top-25 to account for the navbar height, and when the keyboard is open, we have less space..
            className="
                fixed inset-0 top-25 flex flex-col items-center justify-center
                space-y-[8dvh] text-center text-xl text-pretty
            "
        >
            {props.children}
        </div>
    );
}

function EmptyStateButton(props: {onClick: () => void; children: React.ReactNode}) {
    return (
        <button
            className="font-medium text-indigo-700"
            onClick={() => {
                void hardware.haptic();
                props.onClick();
            }}
        >
            {props.children}
        </button>
    );
}

function RecentCard(props: {
    result: models["journal.nutrition.SearchResult"];
    onBulkSelect: (
        result: models["journal.nutrition.SearchResult"],
        isSelected: boolean,
    ) => void;
    isSelected: boolean;
    showCheckmark?: boolean;
}) {
    const {result, onBulkSelect, isSelected} = props;
    const calculations = getRoundedCalculations(
        calculateEntryMetrics({
            entry: result.entry,
            quantity: result.entry.quantity,
            unit: result.entry.unit,
        }),
        false,
    );
    const quantity = getRoundedQuantity(result.entry.quantity);

    return (
        <button
            className="block w-full rounded-lg bg-white px-4 py-3 text-left"
            onClick={() => {
                void hardware.haptic();
                onBulkSelect(result, isSelected);
            }}
            aria-label={result.entry.name}
        >
            <div className="flex items-center gap-2">
                {isSelected === true ? (
                    <div
                        className={classNames(
                            "size-7.5 items-center justify-center rounded-full bg-indigo-50",
                            props.showCheckmark === true ? "flex" : "hidden",
                        )}
                    >
                        <CheckIcon
                            className="size-4 text-indigo-600"
                            aria-hidden="true"
                        />
                    </div>
                ) : (
                    <div
                        className={classNames(
                            "size-7.5 items-center justify-center rounded-full border-2 border-stone-200",
                            props.showCheckmark === true ? "flex" : "hidden",
                        )}
                    ></div>
                )}
                <div className="min-w-0 flex-1">
                    <div className="truncate text-base/5 font-normal">
                        {result.entry.name}
                    </div>
                    <div className="mt-0.5 text-sm text-gray-700">
                        {quantity} {result.entry.unit} • {calculations.calories} cal
                    </div>
                </div>
            </div>
        </button>
    );
}

function PredictionCard(props: {
    result: models["journal.nutrition.SearchResult"];
    onBulkSelect: (locator: EntryLocator) => void;
    isSelected: boolean;
    showCheckmark?: boolean;
    isInitialMount: boolean;
    index: number;
    layout?: boolean;
}) {
    const {result, onBulkSelect, isSelected, isInitialMount, index, layout} = props;

    return (
        <motion.button
            layout={layout}
            initial={isInitialMount ? {opacity: 0, scale: 0.95} : false}
            animate={{opacity: 1, scale: 1}}
            transition={{
                layout: {duration: 0.2, ease: "easeOut"},
                delay: isInitialMount ? 0.12 + index * 0.03 : 0,
                duration: 0.15,
                ease: "easeOut",
            }}
            className={classNames(
                "rounded-lg p-3 text-left text-sm/4.5 font-normal",
                isSelected === true
                    ? "bg-indigo-50 ring-1 ring-indigo-400"
                    : "bg-white",
            )}
            onClick={() => {
                void hardware.haptic();
                onBulkSelect(`${result.backend_id}:${result.backend}`);
            }}
        >
            {result.entry.name}
        </motion.button>
    );
}

function getLayoutId(result: models["journal.nutrition.SearchResult"]) {
    return `recent-card-${result.backend_id}-${result.backend}`;
}

// Progressive rendering hook - renders items in small batches via the motion frame loop.
function useProgressiveList<T>(
    items: T[],
    initialBatch: number,
    resetKey: string | number | boolean,
): T[] {
    const [visibleCount, setVisibleCount] = React.useState(() =>
        Math.min(initialBatch, items.length),
    );
    const delayStartRef = React.useRef<number | null>(null);
    const delayMs = 1000;

    // Reset when items change significantly (e.g., filter or root changes).
    React.useEffect(() => {
        delayStartRef.current = null;
        setTimeout(() => {
            setVisibleCount(items.length);
        }, delayMs);
    }, [items.length, initialBatch, resetKey]);

    return items.slice(0, visibleCount);
}

function DetailView(props: {
    setSelectedRootLocator: (locator: EntryLocator | null) => void;
    selectedRootHandler: SelectedRootHandler;
    results: models["journal.nutrition.SearchResult"][];
    filter: string;
    initialBatch: number;
}) {
    const {selectedRootHandler, results} = props;
    const isInitialMount = React.useRef(true);

    React.useEffect(() => {
        isInitialMount.current = false;
    }, []);

    const selectedRootResult = locatorToSearchResult(
        selectedRootHandler.selectedRootLocator,
        results,
    );

    const commonCoresResults = selectedRootHandler.commonCores
        .map((locator) => locatorToSearchResult(locator, results))
        .filter((result) => utils.filterPredicate(result.name, props.filter));

    const allSuggestedItemsResults = selectedRootHandler.suggestedItems
        .map((locator) => locatorToSearchResult(locator, results))
        .filter((result) => utils.filterPredicate(result.name, props.filter))
        .slice(0, 50);
    const detailResetKey = `${selectedRootHandler.selectedRootLocator}-${props.filter}`;
    const suggestedItemsResults = useProgressiveList(
        allSuggestedItemsResults,
        props.initialBatch,
        detailResetKey,
    );

    const allEverythingElseResults = selectedRootHandler.everythingElse
        .map((locator) => locatorToSearchResult(locator, results))
        .filter((result) => utils.filterPredicate(result.name, props.filter))
        .slice(0, 50);
    const everythingElseResults = useProgressiveList(
        allEverythingElseResults,
        props.initialBatch,
        detailResetKey,
    );

    return (
        <div className="space-y-2">
            <motion.div
                layoutId={getLayoutId(selectedRootResult)}
                transition={{
                    layout: {duration: 0.15, ease: "easeOut"},
                }}
            >
                <RecentCard
                    onBulkSelect={() => props.setSelectedRootLocator(null)}
                    isSelected={true}
                    result={selectedRootResult}
                    showCheckmark={true}
                />
            </motion.div>

            {selectedRootHandler.commonCores.length > 0 && (
                <motion.div
                    initial={isInitialMount.current ? {opacity: 0, y: 8} : false}
                    animate={{opacity: 1, y: 0}}
                    transition={{delay: 0.1, duration: 0.2, ease: "easeOut"}}
                >
                    <h3 className="mb-2 text-lg">Frequent additions</h3>
                    <div className="grid grid-cols-2 gap-2">
                        {commonCoresResults.map((result, index) => (
                            <PredictionCard
                                key={result.entry.uuid}
                                index={index}
                                result={result}
                                onBulkSelect={(locator) =>
                                    selectedRootHandler.toggleCommonCore(locator)
                                }
                                isSelected={selectedRootHandler.selectedLocators.includes(
                                    result.locator,
                                )}
                                isInitialMount={isInitialMount.current}
                            />
                        ))}
                    </div>
                </motion.div>
            )}

            {selectedRootHandler.suggestedItems.length > 0 && (
                <motion.div
                    initial={isInitialMount.current ? {opacity: 0, y: 8} : false}
                    animate={{opacity: 1, y: 0}}
                    transition={{delay: 0.25, duration: 0.2, ease: "easeOut"}}
                >
                    <h3 className="mb-2 text-lg">Suggested items</h3>
                    <div className="grid grid-cols-2 gap-2">
                        {suggestedItemsResults.map((result, index) => (
                            <PredictionCard
                                key={result.entry.uuid}
                                index={index}
                                result={result}
                                onBulkSelect={(locator) =>
                                    selectedRootHandler.toggleSuggestedItem(locator)
                                }
                                isSelected={selectedRootHandler.selectedLocators.includes(
                                    result.locator,
                                )}
                                isInitialMount={isInitialMount.current}
                                layout
                            />
                        ))}
                    </div>
                </motion.div>
            )}
            {selectedRootHandler.everythingElse.length > 0 && (
                <motion.div
                    initial={isInitialMount.current ? {opacity: 0, y: 8} : false}
                    animate={{opacity: 1, y: 0}}
                    transition={{delay: 0.25, duration: 0.2, ease: "easeOut"}}
                >
                    <div className="grid grid-cols-2 gap-2">
                        {everythingElseResults.map((result, index) => (
                            <PredictionCard
                                key={result.entry.uuid}
                                index={index}
                                result={result}
                                onBulkSelect={(locator) =>
                                    selectedRootHandler.toggleEverythingElse(locator)
                                }
                                isSelected={selectedRootHandler.selectedLocators.includes(
                                    result.locator,
                                )}
                                isInitialMount={isInitialMount.current}
                                layout
                            />
                        ))}
                    </div>
                </motion.div>
            )}
        </div>
    );
}

interface RecentProps {
    results: models["journal.nutrition.SearchResult"][];
    filter: string;
    onSearch: (keywords: string) => void;
    onClearFilter: () => void;
    predictions: PredictionsHandler;
}

function Recent(props: RecentProps) {
    const {filter, onSearch} = props;
    const isInFakeRootMode = props.predictions.results?.isFakeRoot === true;
    const scrollContext = React.useContext(ScrollContainerContext);
    const isTallScreen = useMedia("(min-height: 1024px)", false);
    const initialBatch = isTallScreen ? 24 : 12;
    const detailInitialBatch = isTallScreen ? 24 : 8;

    const results = props.predictions.recent
        .map((locator) => locatorToSearchResult(locator, props.results))
        .concat(
            props.results
                .map((result) => ({
                    ...result,
                    locator: `${result.backend_id}:${result.backend}`,
                }))
                .filter((result) => !props.predictions.recent.includes(result.locator)),
        );

    const allFilteredResults = results
        .filter((result) => utils.filterPredicate(result.name, filter))
        .slice(0, 50);

    // Progressive rendering - show a small batch immediately, rest after 1s.
    const listResetKey = `${filter}-${props.predictions.results == null ? "list" : "detail"}`;
    const filteredResults = useProgressiveList(
        allFilteredResults,
        initialBatch,
        listResetKey,
    );

    const emptyStateContent =
        results.length == 0 ? (
            <>
                <p>You have no recent items.</p>
                <p>This list will grow as you log food.</p>
                <p>You’ll rarely need to search.</p>
                <p>
                    Try searching for a{" "}
                    <EmptyStateButton onClick={() => onSearch("banana")}>
                        banana
                    </EmptyStateButton>{" "}
                    or a{" "}
                    <EmptyStateButton onClick={() => onSearch("mcdonald's big mac")}>
                        McDonald’s Big Mac
                    </EmptyStateButton>
                    .
                </p>
            </>
        ) : // Keyboard makes this really weird because of empty state content being "fixed" so the selected root runs into the text.
        // So we don't do it in those cases.
        filteredResults.length == 0 && props.predictions.results == null ? (
            <>
                <p>No recent items match your filter.</p>
                <p>Search powered by Nutritionix.</p>
            </>
        ) : null;

    return (
        <LayoutGroup>
            {emptyStateContent != null && <EmptyState>{emptyStateContent}</EmptyState>}

            {props.predictions.results != null &&
            !props.predictions.results.isFakeRoot ? (
                <DetailView
                    filter={filter}
                    selectedRootHandler={props.predictions.results}
                    results={props.results}
                    setSelectedRootLocator={props.predictions.setSelectedRootLocator}
                    initialBatch={detailInitialBatch}
                />
            ) : (
                // List view - handles both no selection AND fake root mode
                <div className="space-y-2">
                    {filteredResults.map((result, index) => (
                        <motion.div
                            key={`${result.backend_id}-${result.backend}`}
                            layoutId={getLayoutId(result)}
                            // Skip fade-in for first item to avoid flash when deselecting top item
                            initial={index === 0 ? false : {opacity: 0}}
                            animate={{opacity: 1}}
                            transition={{
                                layout: {duration: 0.15, ease: "easeOut"},
                                opacity: {duration: 0.15, delay: index * 0.02},
                            }}
                        >
                            <RecentCard
                                showCheckmark={isInFakeRootMode}
                                onBulkSelect={(result) => {
                                    void hardware.haptic();
                                    const locator = `${result.backend_id}:${result.backend}`;

                                    if (isInFakeRootMode) {
                                        if (
                                            locator ===
                                            props.predictions.results!
                                                .selectedRootLocator
                                        ) {
                                            // Clicking the root deselects and exits fake root mode
                                            props.predictions.setSelectedRootLocator(
                                                null,
                                            );
                                        } else {
                                            // Toggle other items
                                            props.predictions.results!.toggleEverythingElse(
                                                locator,
                                            );
                                        }
                                    } else if (props.predictions.isRealRoot(locator)) {
                                        // Real root: scroll and select simultaneously
                                        void analytics.track("REAL_ROOT_SELECTED");
                                        scrollContext?.scrollTo(0);
                                        props.onClearFilter();
                                        props.predictions.setSelectedRootLocator(
                                            locator,
                                        );
                                    } else {
                                        // Fake root: select immediately (no scroll)
                                        void analytics.track("FAKE_ROOT_SELECTED");
                                        props.predictions.setSelectedRootLocator(
                                            locator,
                                        );
                                    }
                                }}
                                isSelected={
                                    isInFakeRootMode &&
                                    props.predictions.results!.selectedLocators.includes(
                                        `${result.backend_id}:${result.backend}`,
                                    )
                                }
                                result={result}
                            />
                        </motion.div>
                    ))}
                </div>
            )}
        </LayoutGroup>
    );
}

interface SearchActionProps {
    onSearch: () => void;
    filter: string;
    forceHide?: boolean;
}

function SearchAction(props: SearchActionProps) {
    if (props.forceHide == true || props.filter == "") {
        return null;
    }

    const title = (
        <span>
            Search database for <span className="font-bold">{props.filter}</span>
        </span>
    );

    return (
        <div className="flex justify-center">
            <button
                aria-label={`Search database for ${props.filter}`}
                type="button"
                className="
                    flex max-w-80 items-center gap-1.5 rounded-lg bg-indigo-600 px-5
                    py-2 text-sm font-normal text-white
                "
                onClick={() => {
                    void hardware.haptic();
                    props.onSearch();
                }}
            >
                <Icon icon="search" />
                <span className="truncate">{title}</span>
            </button>
        </div>
    );
}

function BulkSummary(props: {
    stage: StageHandler<server.journal.nutrition.ExpandedEntry.output>;
    lastSearched: string;
    predictions: PredictionsHandler;
    filter: string;
    onSearch: (filter: string) => void;
    onClear: () => void;
    onNext: () => void;
}) {
    const itemCount = props.predictions.results?.selectedLocators.length ?? 0;

    const search =
        props.filter == "" ? null : (
            <SearchAction
                onSearch={() => props.onSearch(props.filter)}
                forceHide={props.lastSearched == props.filter}
                filter={props.filter}
            />
        );

    if (itemCount == 0 && search == null) {
        return null;
    }

    return (
        <div>
            {search}
            {itemCount > 0 && (
                <div
                    className="
                        mx-4 mt-2 flex items-center gap-4 rounded-2xl border
                        border-gray-100 bg-white p-3
                    "
                >
                    <button
                        className={classNames(
                            BASE_ACTION_BAR_BUTTON_CLASS,
                            "ring-1 ring-inset ring-gray-300 text-gray-700",
                        )}
                        type="button"
                        onClick={() => {
                            void hardware.haptic();
                            props.onClear();
                        }}
                    >
                        Clear
                    </button>
                    <div className="flex-1">
                        {itemCount} {utils.pluralize(itemCount, "item", "items")}{" "}
                        selected
                    </div>
                    <button
                        className={ACTION_BAR_BUTTON_CLASS}
                        type="button"
                        onClick={() => {
                            void hardware.haptic();
                            props.onNext();
                        }}
                    >
                        Next
                    </button>
                </div>
            )}
        </div>
    );
}

interface SearchResultsProps {
    results: models["journal.nutrition.SearchResult"][];
    isSearching: boolean;
    profile: server.core.rpc.Profile.output;
    stage: StageHandler<server.journal.nutrition.ExpandedEntry.output>;
    onSelect: (result: models["journal.nutrition.SearchResult"]) => void;
    onSearch: () => void;
    filter: string;
}

function SearchResults(props: SearchResultsProps) {
    const {profile, results} = props;

    const entries = props.isSearching
        ? []
        : results.map((result) => (
              <div
                  key={`${result.backend_id}-${result.backend}`}
                  className="rounded-lg bg-white px-4 py-3"
              >
                  <SearchResultCard
                      entry={result.entry}
                      hiddenMacros={profile.has_hidden_macros}
                      tag={result.is_cupboard == true ? "Saved" : undefined}
                      link={() => {
                          void hardware.haptic();
                          props.onSelect(result);
                      }}
                  />
              </div>
          ));
    const emptyStateContent = props.isSearching ? (
        <>Searching...</>
    ) : results.length == 0 ? (
        <>
            <p>No results found.</p>
            <p>Try different keywords.</p>
        </>
    ) : null;

    return (
        <>
            {emptyStateContent != null && <EmptyState>{emptyStateContent}</EmptyState>}
            <div className="space-y-2">{entries}</div>
        </>
    );
}

interface SearchBoxProps {
    filter: string;
    onChange: (value: string) => void;
    onSearch: () => void;
    onChangeSearchFilter: (value: string) => void;
    currentTab: "database" | "recent";
    highlightIcons?: boolean;
    cameraEnabled: boolean;
    close: (result: routes.EntryJourneyOutput) => void;
    switchTab: (tab: "recent" | "database" | "foods" | "recipes") => void;
}

function FrostRing(props: {show: boolean; delay: number}) {
    return (
        <motion.span
            initial={{opacity: 0}}
            animate={props.show ? {opacity: [0, 1, 0.4, 1, 0.4, 1, 0.4, 0]} : undefined}
            transition={{
                duration: 7,
                ease: "easeInOut",
                times: [0, 0.08, 0.22, 0.36, 0.5, 0.64, 0.78, 1],
                delay: props.delay,
            }}
            style={{
                position: "absolute",
                inset: -3,
                borderRadius: 12,
                border: "2px solid #818cf8",
                background: "rgba(99, 102, 241, 0.15)",
                boxShadow: "0 0 6px rgba(99, 102, 241, 0.25)",
                pointerEvents: "none",
            }}
        />
    );
}

function SearchBox(props: SearchBoxProps) {
    const showFrost = props.highlightIcons === true;

    return (
        <form
            onSubmit={(event) => {
                event.preventDefault();
                props.onSearch();
            }}
            className="flex gap-2 pb-2"
        >
            <div className="grid flex-1 grid-cols-1 rounded-lg bg-white">
                <input
                    enterKeyHint="search"
                    type="search"
                    name=""
                    className="
                        col-start-1 row-start-1 block w-full appearance-none rounded-lg
                        border-none bg-white py-1.5 pr-3 pl-13 text-base text-gray-900
                        outline-none
                        placeholder:text-gray-500
                        focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600
                    "
                    placeholder={"Enter keywords"}
                    onChange={(event) => props.onChangeSearchFilter(event.target.value)}
                    onFocus={(event) => {
                        if (props.currentTab === "recent") {
                            event.target.select();
                        }
                    }}
                    value={props.filter}
                    spellCheck={false}
                />
                <SimpleIcon
                    icon="search"
                    className="
                        pointer-events-none col-start-1 row-start-1 ml-4 size-5.5
                        self-center text-gray-700
                    "
                />
            </div>
            {props.cameraEnabled && (
                <motion.button
                    type="button"
                    aria-label="Camera"
                    className="relative rounded-lg bg-white p-2"
                    animate={
                        showFrost
                            ? {
                                  color: [
                                      "#374151",
                                      "#4f46e5",
                                      "#374151",
                                      "#4f46e5",
                                      "#374151",
                                      "#4f46e5",
                                      "#374151",
                                      "#374151",
                                  ],
                              }
                            : undefined
                    }
                    transition={{
                        duration: 7,
                        ease: "easeInOut",
                        times: [0, 0.08, 0.22, 0.36, 0.5, 0.64, 0.78, 1],
                        delay: 0.25,
                    }}
                    onClick={() => {
                        void hardware.haptic();
                        props.close({type: "switch_to_camera", captureType: "meal"});
                    }}
                >
                    <FrostRing show={showFrost} delay={0.25} />
                    <SimpleIcon
                        icon="custom-camera-bigger-hero-icon"
                        className="relative size-6"
                    />
                </motion.button>
            )}
            <motion.button
                type="button"
                aria-label="Barcode"
                className="relative rounded-lg bg-white p-2"
                animate={
                    showFrost
                        ? {
                              color: [
                                  "#374151",
                                  "#4f46e5",
                                  "#374151",
                                  "#4f46e5",
                                  "#374151",
                                  "#4f46e5",
                                  "#374151",
                                  "#374151",
                              ],
                          }
                        : undefined
                }
                transition={{
                    duration: 7,
                    ease: "easeInOut",
                    times: [0, 0.08, 0.22, 0.36, 0.5, 0.64, 0.78, 1],
                    delay: 0.25,
                }}
                onClick={() => {
                    void hardware.haptic();
                    props.close({type: "switch_to_camera", captureType: "barcode"});
                }}
            >
                <FrostRing show={showFrost} delay={0.25} />
                <span className="relative">
                    <Icon large="xl" icon="custom-barcode" />
                </span>
            </motion.button>
            <input type="submit" hidden />
        </form>
    );
}

function useSearchHooks(props: {
    currentTab: "database" | "recent" | "foods" | "recipes";
}) {
    const dispatch = useAppDispatch();
    const context = useSiteContext();

    const selectedStore = props.currentTab;

    React.useEffect(() => {
        dispatch(changeLastUsedStore(selectedStore));
    }, [selectedStore]);

    const state = useAppSelector((state) => ({
        results: state.search,
        filter: state.search.filter,
        isBulkEnabled: state.search.isBulkEnabled,
        isSearching: state.search.isSearching,
    }));

    const actions = bindActionCreators(
        {
            changeSearchFilter,
            changeUSDA,
            fetchSearch,
            toggleBulk,
        },
        dispatch,
    );

    return {
        ...state,
        router: routes.router,
        actions,
        profile: context.profile,
    };
}

function getInitial(props: {
    foodFromSearch: {
        upc: string | null;
        quantity: DecimalValue | null;
        unit: string | null;
    } | null;
    stage: StageHandler<server.journal.nutrition.ExpandedEntry.output>;
}) {
    const {foodFromSearch} = props;

    if (foodFromSearch != null) {
        const staged = [props.stage.data.entries[0], null][0];

        // When creating a product from a not found UPC.
        if (foodFromSearch.upc != null) {
            return {
                base_unit: "" as const,
                upc: foodFromSearch.upc,
                from_missing_upc: true,
            };
        }

        if (staged != null) {
            // Example: we may get a food like ice cream that is 0.5 cups.
            // If we just use the numerator, we'll get 1 and denominator 1.
            // But the actual serving is numerator / denominator. So we create
            // the actual fraction here.
            // But we may come up with something like .6600000262260437
            // So we limit to 2 decimal places at most.
            const servingSizeNumerator = new Decimal(staged.serving_size_numerator)
                .dividedBy(staged.serving_size_denominator)
                .toDecimalPlaces(2)
                .toString();

            return {
                ...staged,
                base_unit: staged.base_unit == "grams" ? "g" : staged.base_unit,
                serving_size_numerator: servingSizeNumerator,
                from_nutritionix_backend_id: staged.backend_id.toString(),
            };
        }
    }

    return {
        base_unit: "" as const,
    };
}

export const Search = routes.Search.useConditions((props) => {
    const searchHooks = useSearchHooks(props);

    const [lastSearched, setLastSearched] = React.useState(searchHooks.filter);

    if (
        props.currentTab == "foods" ||
        props.currentTab == "recipes" ||
        searchHooks.profile == null
    ) {
        throw new Error("Not implemented");
    }

    const handleSearch = (forceQuery?: string) => {
        if ((props.predictions.results?.selectedLocators.length ?? 0) > 0) {
            void analytics.track("SEARCHED_AFTER_PREDICTIONS");
        }
        const query = forceQuery ?? searchHooks.filter;
        searchHooks.actions.changeSearchFilter(query);
        void searchHooks.actions.fetchSearch({keywords: query});
        props.switchTab("database");
        setLastSearched(query);
    };

    const topSection = (
        <SearchBox
            currentTab={props.currentTab}
            filter={searchHooks.filter}
            onChange={searchHooks.actions.changeSearchFilter}
            onSearch={handleSearch}
            onChangeSearchFilter={searchHooks.actions.changeSearchFilter}
            highlightIcons={props.input.highlightIcons}
            cameraEnabled={props.input.cameraEnabled !== false}
            close={props.close}
            switchTab={props.switchTab}
        />
    );

    const bottomSection = (
        <BulkSummary
            stage={props.stage}
            predictions={props.predictions}
            filter={searchHooks.filter}
            lastSearched={lastSearched}
            onSearch={handleSearch}
            onClear={() => {
                void analytics.track("CLEARED_PREDICTIONS");
                props.stage.setEntries([]);
                setLastSearched("");
                props.predictions.clearSelections();
                searchHooks.actions.changeSearchFilter("");
            }}
            onNext={() => {
                loadEntriesIntoStage(
                    props.predictions.results?.selectedLocators ?? [],
                    searchHooks.results.stores.recent,
                    props.stage,
                );
                void routes.router.push("create.macros", {});
            }}
        />
    );

    if (props.currentTab == "recent") {
        return {
            view: (
                <Recent
                    results={searchHooks.results.stores.recent}
                    predictions={props.predictions}
                    onSearch={(query) => {
                        handleSearch(query);
                    }}
                    onClearFilter={() => {
                        searchHooks.actions.changeSearchFilter("");
                    }}
                    filter={searchHooks.filter}
                />
            ),
            actions: [],
            options: {
                bottomSection,
                title: "Log Food",
                topSection,
                back:
                    props.input.target?.type == "DAY"
                        ? "Journal"
                        : props.input.target?.type == "RECIPE"
                          ? "Recipe"
                          : "Journal",
            },
        };
    }

    return {
        view: (
            <SearchResults
                results={searchHooks.results.stores.database}
                isSearching={searchHooks.isSearching}
                profile={searchHooks.profile}
                stage={props.stage}
                filter={searchHooks.filter}
                onSearch={handleSearch}
                onSelect={(result) => {
                    if ((props.predictions.results?.selectedLocators.length ?? 0) > 0) {
                        void analytics.track("SELECTED_RESULT_AFTER_PREDICTIONS");
                    }
                    loadEntriesIntoStage(
                        props.predictions.results?.selectedLocators ?? [],
                        searchHooks.results.stores.recent,
                        props.stage,
                        result,
                    );
                    void routes.router.push("create.macros", {});
                }}
            />
        ),
        actions: [],
        options: {
            bottomSection,
            title: "Search",
            topSection,
            back: {
                title: "Recent",
                locator: routes.router.locator({
                    route: "search.recent",
                    params: {},
                }),
            },
        },
    };
});

interface CreateEntryProps {
    fromScan: boolean;
    input: routes.EntryJourneyInput;
    stage: StageHandler<ExpandedEntry>;
    close: (result: routes.EntryJourneyOutput | null) => void;
    setFoodFromSearch: (
        value: {
            quantity: DecimalValue | null;
            unit: string | null;
            upc: string | null;
        } | null,
    ) => void;
}

function useCreateEntryScene(props: CreateEntryProps) {
    const showQuickSelect = props.fromScan && props.input.target?.type == "DAY";

    const view = (
        <div style={{display: "flex", flexDirection: "column", gap: 10}}>
            {showQuickSelect && (
                <QuickSelect
                    name=""
                    type="text"
                    options={[
                        {value: "now", label: "Use this now"},
                        {value: "cupboard", label: "Save for later"},
                    ]}
                    onChange={(value) => {
                        const method = value === "now" ? "now" : "cupboard";

                        props.stage.setMethod(method);
                    }}
                    value={props.stage.data.method}
                    shrinkLast={false}
                />
            )}
            {props.stage.data.entries.length == 1 ? (
                props.stage.entries
            ) : (
                <div
                    className="
                        space-y-4
                        *:rounded-lg *:bg-white *:px-4 *:py-3
                    "
                >
                    {props.stage.mealSelector}
                    {props.stage.entries}
                </div>
            )}
        </div>
    );

    const logAction: ActionDefinition = {
        icon: "0821-check",
        title: props.stage.data.entries.length == 1 ? "Log entry" : "Log entries",
        onClick: () => {
            const results = props.stage.validate();

            if (results == null) {
                return;
            }

            props.close({
                type: "entries",
                entries: results.entries,
                meal: results.meal,
                method: props.stage.data.method,
            });
        },
    };

    const makeCorrections = (
        correctionsRoute:
            | "create_food.macros"
            | "barcode_found_make_corrections.macros",
    ): ActionDefinition | null =>
        props.stage.data.entries.length === 1 &&
        props.stage.data.entries[0].backend === "nutritionix_v2"
            ? {
                  icon: "edit",
                  title: "Adjust macros",
                  onClick: () => {
                      const {quantity, unit} = props.stage.data.entries[0];

                      props.setFoodFromSearch({
                          quantity,
                          unit,
                          upc: null,
                      });
                      routes.router.push(correctionsRoute, {});
                  },
              }
            : null;

    return {view, logAction, makeCorrections};
}

export const Create = routes.Create.useConditions((props) => {
    const {lastUsedStore} = useAppSelector((state) => ({
        lastUsedStore: state.search.lastUsedStore,
    }));
    const {view, logAction, makeCorrections} = useCreateEntryScene(props);

    const tabs: ConditionsTab[] = [
        {
            view,
            title: "Macros",
            icon: "bullseye",
            hasErrors: props.stage.hasErrors,
        },
    ];

    if (props.stage.ingredients != false) {
        tabs.push({
            view: <>{props.stage.ingredients}</>,
            title: "Recipe",
            icon: "cutlery",
        });
    }

    return {
        tabs,
        actions: [makeCorrections("create_food.macros"), logAction],
        options: {
            title:
                props.input.target?.type == "RECIPE"
                    ? "Add Ingredient"
                    : "Create Entry",
            actionsStyle: "contrast",
            back: {
                locator: routes.router.locator({
                    route: `search.${lastUsedStore}`,
                    params: {},
                }),
                title: "Back",
            },
        },
    };
});

export const BarcodeFoundConfirmEntry = routes.BarcodeFoundConfirmEntry.useConditions(
    (props) => {
        const {view, logAction, makeCorrections} = useCreateEntryScene(props);

        return {
            view,
            actions: [
                makeCorrections("barcode_found_make_corrections.macros"),
                logAction,
            ],
            options: {
                title: "Create Entry",
                actionsStyle: "contrast",
                back: "Cancel",
            },
        };
    },
);

export const BarcodeNotFoundConfirmEntry =
    routes.BarcodeNotFoundConfirmEntry.useConditions((props) => {
        const {view, logAction} = useCreateEntryScene(props);

        return {
            view,
            actions: [logAction],
            options: {
                title: "Create Entry",
                actionsStyle: "contrast",
                back: "Cancel",
            },
        };
    });

interface CreateFoodProps {
    stage: StageHandler<ExpandedEntry>;
    foodFromSearch: {
        upc: string | null;
        quantity: DecimalValue | null;
        unit: string | null;
    } | null;
    setFoodFromSearch: (
        value: {
            upc: string | null;
            quantity: DecimalValue | null;
            unit: string | null;
        } | null,
    ) => void;
    setFromScan: (value: boolean) => void;
}

function useCreateFoodScene(
    props: CreateFoodProps,
    nextRoute:
        | "create.macros"
        | "barcode_not_found_confirm_entry.macros"
        | "barcode_found_confirm_entry.macros",
) {
    const dispatch = useAppDispatch();
    const foodForms = foods.useFoodForms({initial: getInitial(props)});

    const tabs: ConditionsTab[] = [
        {
            view: <>{foodForms.basicLayout.component}</>,
            title: "Macros",
            icon: "bullseye",
            hasErrors: foodForms.basicLayout.hasErrors,
        },
        {
            view: <>{foodForms.advancedLayout.component}</>,
            title: "Advanced",
            icon: "list",
            hasErrors: foodForms.advancedLayout.hasErrors,
        },
    ];

    const saveAction: ActionDefinition = {
        icon: "save",
        title: "Save",
        onClick: async () => {
            const result = await forms.submitForm(
                server.journal.nutrition.create_food,
                foodForms.form.values,
                foodForms.form,
            );
            if (result.type !== "success") {
                return;
            }

            props.setFoodFromSearch(null);

            const {foodFromSearch} = props;
            if (foodFromSearch == null) {
                return;
            }

            const created = result.data.food;
            const searchResult = result.data.search_result;

            const hasSameUnit =
                foodFromSearch.unit != null &&
                searchResult.entry.units.find(
                    (unit) => unit.unit == foodFromSearch.unit,
                ) != null;

            const resultWithQuantityAndUnit = hasSameUnit
                ? {
                      ...searchResult,
                      entry: {
                          ...searchResult.entry,
                          quantity: foodFromSearch.quantity ?? "",
                          unit: foodFromSearch.unit ?? "",
                      },
                  }
                : searchResult;

            props.stage.setEntries([resultWithQuantityAndUnit.entry]);
            props.setFromScan(foodFromSearch.upc != null);

            dispatch(
                notify(
                    nextRoute === "create.macros"
                        ? "Your corrected food was created.\nWe are taking you back to the search so you can use it."
                        : "Your food was created.",
                ),
            );
            void analytics.track("CREATED_FOOD", {
                from_missing_upc:
                    created.from_missing_upc === true ? created.upc : undefined,
                from_nutritionix_backend_id: created.from_nutritionix_backend_id,
            });
            void routes.router.push(nextRoute, {});
        },
    };

    return {tabs, saveAction};
}

export const CreateFood = routes.CreateFood.useConditions((props) => {
    const {tabs, saveAction} = useCreateFoodScene(props, "create.macros");

    return {
        tabs,
        actions: [saveAction],
        options: {
            title: "Create Food",
            actionsStyle: "contrast",
            back: {
                title: "Back",
                locator: routes.router.locator({
                    route: "create.macros",
                    params: {},
                }),
            },
        },
    };
});

export const BarcodeNotFoundCreateFood = routes.BarcodeNotFoundCreateFood.useConditions(
    (props) => {
        const {tabs, saveAction} = useCreateFoodScene(
            props,
            "barcode_not_found_confirm_entry.macros",
        );

        return {
            tabs,
            actions: [saveAction],
            options: {
                title: "Create Food",
                actionsStyle: "contrast",
                back: "Cancel",
            },
        };
    },
);

export const BarcodeFoundMakeCorrections =
    routes.BarcodeFoundMakeCorrections.useConditions((props) => {
        const {tabs, saveAction} = useCreateFoodScene(
            props,
            "barcode_found_confirm_entry.macros",
        );

        return {
            tabs,
            actions: [saveAction],
            options: {
                title: "Create Food",
                actionsStyle: "contrast",
                back: {
                    title: "Back",
                    locator: routes.router.locator({
                        route: "barcode_found_confirm_entry.macros",
                        params: {},
                    }),
                },
            },
        };
    });
